import { Link } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";

/**
 * Placeholder console for Supabase → ERPNext document sync. All controls stay disabled until
 * the team turns on push (`ERPNEXT_PUSH_ENABLED`) and wires this page to real APIs.
 */
const SYNC_ENTITY_ROWS: { entity: string; label: string }[] = [
  { entity: "supplier", label: "Supplier" },
  { entity: "item-group", label: "Item group" },
  { entity: "item", label: "Item" },
  { entity: "customer", label: "Customer" },
  { entity: "rep-agency", label: "Rep agency" },
  { entity: "rep-staff", label: "Rep staff" },
  { entity: "purchase-order", label: "Purchase order" },
  { entity: "purchase-invoice", label: "Purchase invoice" },
  { entity: "sales-order", label: "Sales order" },
  { entity: "sales-order-shipment", label: "Sales order shipment" },
  { entity: "sales-order-invoice", label: "Sales order invoice" },
  { entity: "sales-invoice", label: "Sales invoice" },
  { entity: "delivery-note", label: "Delivery note" },
  { entity: "stock-entry", label: "Stock entry" },
  { entity: "build-order", label: "Build order" },
  { entity: "location", label: "Location" },
];

export function ErpNextSync() {
  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">ERPNext Sync</h1>
          <p className="text-sm text-slate-600">
            <Link to="/erp" className="text-brand-700 hover:underline">
              Ingest
            </Link>
          </p>
        </div>
      </header>

      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
        ERPNext push is not available from this page yet. Controls below are a layout preview only;
        nothing here calls the sync service or ERPNext.
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Push status</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-sm text-slate-600">
              When enabled, the integrator may write pending rows from Supabase to ERPNext according
              to each entity sync job.
            </p>
            <div className="flex items-center gap-3">
              <Label className="text-slate-700">Allow ERPNext push</Label>
              <button
                type="button"
                disabled
                aria-disabled="true"
                className="relative inline-flex h-6 w-11 shrink-0 cursor-not-allowed rounded-full bg-slate-200 opacity-60"
              >
                <span className="pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow" />
              </button>
              <span className="text-sm text-slate-500">Off (preview)</span>
            </div>
            <div>
              <Label htmlFor="erpnext-sync-environment">Environment note</Label>
              <Input
                id="erpnext-sync-environment"
                disabled
                value="ERPNEXT_PUSH_ENABLED — not wired to this UI yet"
                readOnly
                className="mt-1 bg-slate-50 text-slate-600"
              />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Run options</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" disabled>
                Run all pending
              </Button>
              <Button type="button" size="sm" variant="secondary" disabled>
                Dry run (preview diff)
              </Button>
            </div>
            <div>
              <Label htmlFor="erpnext-sync-max">Max records per run</Label>
              <Input
                id="erpnext-sync-max"
                type="number"
                disabled
                defaultValue={600}
                className="mt-1 max-w-[12rem]"
              />
            </div>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Entities</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="mb-3 text-sm text-slate-600">
            One row per sync target (matches the integrator CLI). Run actions will attach here later.
          </p>
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-slate-700">Entity</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-700">Last ERP write</th>
                  <th className="px-3 py-2 text-right font-medium text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {SYNC_ENTITY_ROWS.map((row) => (
                  <tr key={row.entity}>
                    <td className="px-3 py-2">
                      <span className="font-mono text-xs text-slate-800">{row.entity}</span>
                      <span className="ml-2 text-slate-500">{row.label}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-400">—</td>
                    <td className="px-3 py-2 text-right">
                      <Button type="button" size="sm" variant="secondary" disabled className="min-w-[5.5rem]">
                        Sync now
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent sync activity</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="mb-3 text-sm text-slate-600">
            Placeholder for a timeline or table of ERP sync attempts (success, skipped, errors).
          </p>
          <div className="space-y-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
            No activity loaded — this section is disabled.
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
